// ============================================
// MARKETING.JS — Calendario, campañas, biblioteca, recursos, credenciales, métricas
// ============================================

import React, { useState, useMemo, useEffect } from 'react';
import { BarraBusqueda, FiltrosRapidos, ContadorResultados } from './utils/filtros';
import { ComposedChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell } from 'recharts';
import { API_BASE } from './config/api';
import { OPC_DINERO } from './utils/dinero';

const PP = "'Poppins', sans-serif";
const colores = { principal: '#5c9adb', oscuro: '#2f5f9e', gris: '#4e5357', grisClaro: '#5e666e', amarillo: '#dcde74', verde: '#5cd4b0', rojo: '#e05c5c', morado: '#a78bfa', fondo: '#f3f4f4' };

const redesSociales = [
  { id: 'instagram', label: 'Instagram', icon: '📷', color: '#E1306C' },
  { id: 'facebook', label: 'Facebook', icon: '📘', color: '#1877F2' },
  { id: 'linkedin', label: 'LinkedIn', icon: '💼', color: '#0A66C2' },
  { id: 'tiktok', label: 'TikTok', icon: '🎵', color: '#000000' },
  { id: 'whatsapp', label: 'WhatsApp Business', icon: '💬', color: '#25D366' },
  { id: 'email', label: 'Correo', icon: '✉️', color: '#EA4335' },
  { id: 'gads', label: 'Google Ads', icon: '💰', color: '#4285F4' },
  { id: 'meta', label: 'Meta Ads', icon: '📊', color: '#0668E1' },
];

const estatusPub = { borrador: { bg: '#f3f4f4', color: '#5e666e' }, revision: { bg: '#fffbe6', color: '#7a6e00' }, aprobado: { bg: '#e6f1fb', color: '#2f5f9e' }, publicado: { bg: '#e8faf5', color: '#1D9E75' }, rechazado: { bg: '#fde8e8', color: '#e05c5c' } };

const hoy = new Date();
const fmt = (d) => d.toISOString().split('T')[0];
const hoyStr = fmt(hoy);

// ============================================
// DATOS
// ============================================
const publicacionesInit = [
  { id: 1, titulo: 'Caso de éxito Bodegas Alianza', redes: ['instagram', 'facebook', 'linkedin'], fecha: hoyStr, hora: '10:00', tipo: 'Carrusel', copy: 'Instalamos 24 cámaras 4K en la nueva bodega de Monterrey. Sistema completo con acceso remoto.', hashtags: '#CCTV #Seguridad #BeepMX', campana: 'Casos de éxito Q1', responsable: 'Carlos V.', estatus: 'publicado' },
  { id: 2, titulo: 'Promo mantenimiento trimestral', redes: ['instagram', 'facebook'], fecha: hoyStr, hora: '14:00', tipo: 'Post', copy: '¿Tu sistema de seguridad necesita revisión? Agenda tu mantenimiento preventivo con 20% de descuento.', hashtags: '#Mantenimiento #Seguridad', campana: 'Promo Q2', responsable: 'Carlos V.', estatus: 'aprobado' },
  { id: 3, titulo: 'Video instalación time-lapse', redes: ['instagram', 'tiktok'], fecha: fmt(new Date(hoy.getTime() + 86400000)), hora: '09:00', tipo: 'Reel', copy: 'Así instalamos un sistema completo en 1 día.', hashtags: '#Instalación #TimeLapse', campana: 'Casos de éxito Q1', responsable: 'Carlos V.', estatus: 'revision' },
  { id: 4, titulo: 'Newsletter marzo', redes: ['email'], fecha: fmt(new Date(hoy.getTime() + 2 * 86400000)), hora: '08:00', tipo: 'Email', copy: 'Resumen mensual de novedades, casos de éxito y tips de seguridad.', hashtags: '', campana: 'Newsletter mensual', responsable: 'Ana M.', estatus: 'borrador' },
  { id: 5, titulo: 'Campaña Google Ads Q2', redes: ['gads'], fecha: hoyStr, hora: '00:00', tipo: 'Anuncio', copy: 'Instalación de cámaras CCTV en CDMX y Monterrey. Cotización sin compromiso.', hashtags: '', campana: 'Ads Q2', responsable: 'Carlos V.', estatus: 'publicado' },
  { id: 6, titulo: 'Post acceso biométrico', redes: ['linkedin'], fecha: fmt(new Date(hoy.getTime() - 86400000)), hora: '12:00', tipo: 'Post', copy: 'Control de acceso facial y huella digital para empresas. Tecnología ZKTeco.', hashtags: '#ControlAcceso #Biométrico', campana: 'Casos de éxito Q1', responsable: 'Carlos V.', estatus: 'publicado' },
  { id: 7, titulo: 'Story detrás de cámaras', redes: ['instagram'], fecha: hoyStr, hora: '16:00', tipo: 'Story', copy: 'Día de trabajo en campo con el equipo Beep.', hashtags: '#BTS #BeepMX', campana: null, responsable: 'Carlos V.', estatus: 'borrador' },
  { id: 8, titulo: 'Anuncio Meta remarketing', redes: ['meta'], fecha: hoyStr, hora: '00:00', tipo: 'Anuncio', copy: '¿Visitaste nuestro sitio? Cotiza tu sistema de seguridad ahora.', hashtags: '', campana: 'Ads Q2', responsable: 'Ana M.', estatus: 'aprobado' },
  { id: 9, titulo: 'WhatsApp broadcast clientes', redes: ['whatsapp'], fecha: fmt(new Date(hoy.getTime() + 3 * 86400000)), hora: '10:00', tipo: 'Post', copy: 'Recordatorio de mantenimiento trimestral. Agenda tu cita.', hashtags: '', campana: 'Retención', responsable: 'Carlos V.', estatus: 'borrador' },
  { id: 10, titulo: 'Post tips seguridad', redes: ['facebook', 'instagram'], fecha: fmt(new Date(hoy.getTime() - 2 * 86400000)), hora: '11:00', tipo: 'Post', copy: '5 errores comunes en videovigilancia y cómo evitarlos.', hashtags: '#Tips #Seguridad', campana: null, responsable: 'Carlos V.', estatus: 'publicado' },
  { id: 11, titulo: 'Reel equipo técnico', redes: ['instagram', 'tiktok'], fecha: fmt(new Date(hoy.getTime() + 4 * 86400000)), hora: '15:00', tipo: 'Reel', copy: 'Conoce al equipo que protege a tu empresa.', hashtags: '#Equipo #BeepMX', campana: 'Branding', responsable: 'Carlos V.', estatus: 'revision' },
  { id: 12, titulo: 'Caso éxito Farma Plus', redes: ['linkedin', 'facebook'], fecha: fmt(new Date(hoy.getTime() - 3 * 86400000)), hora: '09:00', tipo: 'Post', copy: 'Protegemos 6 sucursales de Farma Plus con videovigilancia y control de acceso.', hashtags: '#CasoExito', campana: 'Casos de éxito Q1', responsable: 'Ana M.', estatus: 'publicado' },
];

const campanasData = [
  { id: 1, nombre: 'Casos de éxito Q1', objetivo: 'Branding', canales: ['instagram', 'facebook', 'linkedin'], fechaInicio: '2026-01-01', fechaFin: '2026-03-31', presupuesto: 15000, gastado: 12800, estatus: 'Activa' },
  { id: 2, nombre: 'Ads Q2', objetivo: 'Generación de leads', canales: ['gads', 'meta'], fechaInicio: '2026-04-01', fechaFin: '2026-06-30', presupuesto: 30000, gastado: 0, estatus: 'Planeación' },
  { id: 3, nombre: 'Promo Q2', objetivo: 'Promoción', canales: ['instagram', 'facebook', 'whatsapp'], fechaInicio: '2026-04-01', fechaFin: '2026-04-30', presupuesto: 5000, gastado: 0, estatus: 'Planeación' },
];

const bibliotecaData = [
  { id: 1, nombre: 'Foto instalación Bodegas Alianza', categoria: 'fotos', fecha: '2026-03-15', subidoPor: 'Miguel G.', estatus: 'activo' },
  { id: 2, nombre: 'Video timelapse instalación', categoria: 'videos', fecha: '2026-03-20', subidoPor: 'Carlos V.', estatus: 'activo' },
  { id: 3, nombre: 'Brochure servicios 2026', categoria: 'documentos', fecha: '2026-01-10', subidoPor: 'Ana M.', estatus: 'activo' },
  { id: 4, nombre: 'Logo BeepMX horizontal', categoria: 'logos', fecha: '2025-06-01', subidoPor: 'Ricardo V.', estatus: 'activo' },
  { id: 5, nombre: 'Presentación corporativa', categoria: 'presentaciones', fecha: '2026-02-15', subidoPor: 'Ana M.', estatus: 'activo' },
  { id: 6, nombre: 'Foto equipo técnico', categoria: 'fotos', fecha: '2026-03-25', subidoPor: 'Carlos V.', estatus: 'activo' },
  { id: 7, nombre: 'Diseño post Instagram', categoria: 'disenos', fecha: '2026-03-28', subidoPor: 'Carlos V.', estatus: 'activo' },
  { id: 8, nombre: 'Logo BeepMX cuadrado', categoria: 'logos', fecha: '2025-06-01', subidoPor: 'Ricardo V.', estatus: 'activo' },
  { id: 9, nombre: 'Ficha técnica CCTV', categoria: 'documentos', fecha: '2026-01-20', subidoPor: 'Laura S.', estatus: 'activo' },
  { id: 10, nombre: 'Video antiguo oficina', categoria: 'videos', fecha: '2024-08-10', subidoPor: 'Ana M.', estatus: 'archivado' },
];

const catCfg = { fotos: { icon: '📸', color: '#E1306C', label: 'Fotos' }, videos: { icon: '🎥', color: '#FF0000', label: 'Videos' }, disenos: { icon: '🎨', color: colores.morado, label: 'Diseños' }, documentos: { icon: '📄', color: colores.principal, label: 'Documentos' }, logos: { icon: '🏷️', color: colores.oscuro, label: 'Logos' }, presentaciones: { icon: '📊', color: '#f4a261', label: 'Presentaciones' } };

const plataformasData = [
  { id: 1, nombre: 'Instagram', icon: '📷', url: 'https://instagram.com', usuario: 'beepmx', password: '•••• (gestionado fuera del sistema)', categoria: 'Red social', responsable: 'Carlos V.', ultimoAcceso: '2026-03-30', ultimoCambio: '2026-01-15', estatus: 'Activa', notas: 'Cuenta principal empresa' },
  { id: 2, nombre: 'Facebook', icon: '📘', url: 'https://facebook.com', usuario: 'beepmexico', password: '•••• (gestionado fuera del sistema)', categoria: 'Red social', responsable: 'Carlos V.', ultimoAcceso: '2026-03-30', ultimoCambio: '2026-02-10', estatus: 'Activa', notas: '' },
  { id: 3, nombre: 'Google Ads', icon: '💰', url: 'https://ads.google.com', usuario: 'ads@beepmx.com', password: '•••• (gestionado fuera del sistema)', categoria: 'Publicidad', responsable: 'Ana M.', ultimoAcceso: '2026-03-29', ultimoCambio: '2025-12-01', estatus: 'Activa', notas: 'Cuenta MCC principal' },
  { id: 4, nombre: 'Meta Business', icon: '📊', url: 'https://business.facebook.com', usuario: 'admin@beepmx.com', password: '•••• (gestionado fuera del sistema)', categoria: 'Publicidad', responsable: 'Ana M.', ultimoAcceso: '2026-03-28', ultimoCambio: '2026-01-20', estatus: 'Activa', notas: '' },
  { id: 5, nombre: 'Canva', icon: '🎨', url: 'https://canva.com', usuario: 'diseno@beepmx.com', password: '•••• (gestionado fuera del sistema)', categoria: 'Diseño', responsable: 'Carlos V.', ultimoAcceso: '2026-03-30', ultimoCambio: '2025-09-15', estatus: 'Activa', notas: 'Plan Pro anual' },
  { id: 6, nombre: 'Mailchimp', icon: '✉️', url: 'https://mailchimp.com', usuario: 'marketing@beepmx.com', password: '•••• (gestionado fuera del sistema)', categoria: 'Email', responsable: 'Ana M.', ultimoAcceso: '2026-03-25', ultimoCambio: '2025-11-01', estatus: 'Activa', notas: '' },
  { id: 7, nombre: 'Google Analytics', icon: '📈', url: 'https://analytics.google.com', usuario: 'analytics@beepmx.com', password: '•••• (gestionado fuera del sistema)', categoria: 'Analytics', responsable: 'Ana M.', ultimoAcceso: '2026-03-30', ultimoCambio: '2025-10-01', estatus: 'Activa', notas: 'GA4 configurado' },
  { id: 8, nombre: 'Hootsuite (anterior)', icon: '📅', url: 'https://hootsuite.com', usuario: 'social@beepmx.com', password: '**REVOCADA**', categoria: 'Herramienta', responsable: 'Empleado anterior', ultimoAcceso: '2025-06-01', ultimoCambio: '2025-06-15', estatus: 'Inactiva', notas: 'Cuenta cancelada. Empleado desvinculado.' },
];

const diasSemana = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const rolActual = 'Administrador';
const esAdmin = rolActual === 'Administrador' || rolActual === 'Dirección';
const esMarketing = rolActual === 'Marketing' || esAdmin;

// ============================================
// AGENTES IA
// ============================================
const API_URL = API_BASE;   // direccion del backend: config/api.js (fuente unica)

const agentesIA = [
  { id: 'luna',   nombre: 'Luna',   rol: 'Marketing',          emoji: '✨', endpoint: '/api/agente-marketing',
    bg: '#f0f7e6', bgBot: '#d4e89b', eyeColor: '#4a5c00', nameColor: '#94b803', roleColor: '#5a7300', btnBg: '#94b803',
    estado: 'activo',     actividad: 'Monitoreando marketing' },
  { id: 'stella', nombre: 'Stella', rol: 'Redes sociales',     emoji: '⭐', endpoint: '/api/agente-stella',
    bg: '#fde8f0', bgBot: '#f8b5cf', eyeColor: '#6e1033', nameColor: '#E1306C', roleColor: '#8a1540', btnBg: '#E1306C',
    estado: 'activo',     actividad: 'Publicando en redes' },
  { id: 'spark',  nombre: 'Spark',  rol: 'Creatividad y copy', emoji: '⚡', endpoint: '/api/agente-spark',
    bg: '#fff0e8', bgBot: '#ffc4a8', eyeColor: '#7a2c0a', nameColor: '#FF6B35', roleColor: '#a03c10', btnBg: '#FF6B35',
    estado: 'procesando', actividad: 'Generando ideas creativas' },
  { id: 'iris',   nombre: 'Iris',   rol: 'Diseño visual',      emoji: '🌈', endpoint: '/api/agente-iris',
    bg: '#f3effe', bgBot: '#d0c0f0', eyeColor: '#422760', nameColor: '#9B59B6', roleColor: '#5a3980', btnBg: '#9B59B6',
    estado: 'espera',     actividad: 'Lista para diseñar' },
  { id: 'dato',   nombre: 'Dato',   rol: 'Analítica y métricas', emoji: '📊', endpoint: '/api/agente-dato',
    bg: '#e8f1fb', bgBot: '#a8c8ee', eyeColor: '#0a2c52', nameColor: '#185FA5', roleColor: '#0d3a6b', btnBg: '#185FA5',
    estado: 'procesando', actividad: 'Analizando métricas' },
];

const ESTILOS_AGENTES_IA = `
@keyframes marketing-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
@keyframes marketing-float2 { 0%,100%{transform:translateY(-3px)} 50%{transform:translateY(3px)} }
@keyframes marketing-pulse-ring { 0%{box-shadow:0 0 0 0 rgba(224,92,92,0.4)} 70%{box-shadow:0 0 0 8px rgba(224,92,92,0)} 100%{box-shadow:0 0 0 0 rgba(224,92,92,0)} }
@keyframes marketing-shake { 0%,100%{transform:rotate(0)} 25%{transform:rotate(-8deg)} 75%{transform:rotate(8deg)} }
@keyframes marketing-typing-dot { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
`;

function OjosIA({ color, durmiendo }) {
  if (durmiendo) {
    return (
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ width: 10, height: 3, borderRadius: 2, background: color }} />
        <div style={{ width: 10, height: 3, borderRadius: 2, background: color }} />
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 10 }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
    </div>
  );
}

function BadgeEstadoIA({ estado }) {
  const map = {
    activo:     { bg: '#D1FAE5', fg: '#065F46', txt: 'Activo' },
    procesando: { bg: '#FEF3C7', fg: '#92400E', txt: 'Procesando' },
    espera:     { bg: '#E5E7EB', fg: '#4B5563', txt: 'En espera' },
    alerta:     { bg: '#FEE2E2', fg: '#991B1B', txt: '⚠️ Alerta' },
  };
  const s = map[estado] || map.espera;
  return (
    <span style={{
      background: s.bg, color: s.fg,
      fontSize: 10, fontWeight: 700, padding: '3px 9px',
      borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 5,
      animation: estado === 'alerta' ? 'marketing-pulse-ring 1.6s ease-out infinite' : 'none',
    }}>
      {estado === 'procesando' && (
        <span style={{ display: 'inline-flex', gap: 2 }}>
          {[0, 1, 2].map(i => (
            <span key={i} style={{
              width: 4, height: 4, borderRadius: '50%', background: s.fg,
              animation: `marketing-typing-dot 1s ease-in-out infinite`, animationDelay: `${i * 0.15}s`,
            }} />
          ))}
        </span>
      )}
      {s.txt}
    </span>
  );
}

function BotitoIA({ agente, estado, animDelay }) {
  const durmiendo = estado === 'espera';
  const animName = estado === 'procesando' ? 'marketing-shake' : (estado === 'alerta' ? 'marketing-float2' : 'marketing-float');
  const animDur = estado === 'procesando' ? '0.6s' : '3.2s';
  return (
    <div style={{
      width: 78, height: 78, borderRadius: '50%',
      background: agente.bgBot,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: 6, flexShrink: 0,
      animation: `${animName} ${animDur} ease-in-out infinite`,
      animationDelay: `${animDelay}s`,
    }}>
      <OjosIA color={agente.eyeColor} durmiendo={durmiendo} />
      <div style={{ fontSize: 18, lineHeight: 1 }}>{agente.emoji}</div>
    </div>
  );
}

function CardAgenteIA({ agente, animDelay, movil, onAbrirChat }) {
  return (
    <div
      onClick={() => onAbrirChat(agente.id)}
      style={{
        background: agente.bg,
        borderRadius: 20,
        padding: movil ? 16 : 18,
        cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 12,
        boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
        transition: 'transform 0.2s',
        maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden',
      }}
      onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
    >
      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        <BotitoIA agente={agente} estado={agente.estado} animDelay={animDelay} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: agente.nameColor, lineHeight: 1.1 }}>
            {agente.nombre}
          </div>
          <div style={{ fontSize: 11, color: agente.roleColor, fontWeight: 600, marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {agente.rol}
          </div>
          <div style={{ marginTop: 6 }}>
            <BadgeEstadoIA estado={agente.estado} />
          </div>
        </div>
      </div>

      <div style={{
        fontSize: 12, color: agente.nameColor, opacity: 0.85,
        background: 'rgba(255,255,255,0.55)',
        borderRadius: 12, padding: '8px 12px',
        wordBreak: 'break-word',
      }}>
        {agente.actividad}
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); onAbrirChat(agente.id); }}
        style={{
          width: '100%', padding: '10px 14px',
          background: agente.btnBg, color: '#fff',
          border: 'none', borderRadius: 12, fontSize: 13, fontWeight: 700,
          cursor: 'pointer', fontFamily: PP,
        }}
      >
        💬 Abrir chat
      </button>
    </div>
  );
}

// ============================================
// COMPONENTE
// ============================================
function Marketing({ pestanaInicial = 'calendario' }) {
  const [tab, setTab] = useState(pestanaInicial);
  const [publicaciones, setPublicaciones] = useState(publicacionesInit);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [filtroCat, setFiltroCat] = useState('');
  const [mostrarForm, setMostrarForm] = useState(false);
  const [passVisible, setPassVisible] = useState({});
  const [detallecamp, setDetalleCamp] = useState(null);

  const [width] = useState(window.innerWidth);
  const esCelular = width < 768;

  // ========== Estado de chats con agentes IA ==========
  const [agenteActivo, setAgenteActivo] = useState('luna');
  const [chatAbierto, setChatAbierto] = useState(false);
  const [chatsAgentes, setChatsAgentes] = useState({ luna: [], stella: [], spark: [], iris: [], dato: [] });
  const [inputAgente, setInputAgente] = useState('');
  const [enviandoAgente, setEnviandoAgente] = useState(false);

  const abrirChatAgente = (id) => {
    setAgenteActivo(id);
    setChatAbierto(true);
  };

  const enviarMensajeAgente = async () => {
    const texto = inputAgente.trim();
    if (!texto || enviandoAgente) return;
    const agente = agentesIA.find(a => a.id === agenteActivo);
    if (!agente) return;
    const historialActual = chatsAgentes[agenteActivo] || [];
    const nuevoHistorial = [...historialActual, { autor: 'usuario', texto }];
    setChatsAgentes(prev => ({ ...prev, [agenteActivo]: nuevoHistorial }));
    setInputAgente('');
    setEnviandoAgente(true);
    try {
      const token = localStorage.getItem('beep_token');
      const r = await fetch(`${API_URL}${agente.endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ mensaje: texto, historial: historialActual, nombre: 'Usuario' }),
      });
      const data = await r.json();
      const respuesta = data.respuesta || data.error || 'Sin respuesta del agente';
      setChatsAgentes(prev => ({ ...prev, [agenteActivo]: [...nuevoHistorial, { autor: 'agente', texto: respuesta }] }));
    } catch (e) {
      setChatsAgentes(prev => ({ ...prev, [agenteActivo]: [...nuevoHistorial, { autor: 'agente', texto: 'Error al conectar con el agente' }] }));
    } finally {
      setEnviandoAgente(false);
    }
  };

  // Sincronizar la pestaña interna con el prop que viene de la navegación superior
  useEffect(() => {
    if (pestanaInicial) setTab(pestanaInicial);
  }, [pestanaInicial]);

  // ========== CALENDARIO ==========
  if (tab === 'calendario') {
    const mesActual = hoy.getMonth();
    const anio = hoy.getFullYear();
    const primerDia = new Date(anio, mesActual, 1);
    const ultimoDia = new Date(anio, mesActual + 1, 0);
    const celdas = [];
    for (let i = 0; i < primerDia.getDay(); i++) celdas.push(null);
    for (let i = 1; i <= ultimoDia.getDate(); i++) celdas.push(i);

    return (
      <div style={{ fontFamily: PP }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: colores.gris }}>{meses[mesActual]} {anio}</div>
          <button onClick={() => setMostrarForm(true)} style={{ padding: '8px 16px', background: colores.oscuro, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: PP }}>+ Publicación</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
          {diasSemana.map(d => <div key={d} style={{ textAlign: 'center', fontSize: '10px', fontWeight: '600', color: colores.grisClaro, padding: '6px 0' }}>{d}</div>)}
          {celdas.map((dia, i) => {
            if (!dia) return <div key={`e${i}`} />;
            const ds = `${anio}-${String(mesActual + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
            const pubs = publicaciones.filter(p => p.fecha === ds);
            const esHoy = ds === hoyStr;
            return (
              <div key={ds} style={{ padding: '4px', borderRadius: '8px', background: esHoy ? '#e6f1fb' : '#fff', border: esHoy ? `2px solid ${colores.principal}` : '1px solid #f0f0f0', minHeight: esCelular ? '40px' : '70px' }}>
                <div style={{ fontSize: '11px', fontWeight: esHoy ? '700' : '400', color: esHoy ? colores.oscuro : colores.gris, marginBottom: '2px' }}>{dia}</div>
                {pubs.slice(0, esCelular ? 1 : 3).map(p => {
                  const ec = estatusPub[p.estatus] || estatusPub.borrador;
                  return <div key={p.id} style={{ padding: '2px 4px', borderRadius: '4px', background: ec.bg, marginBottom: '1px', fontSize: '8px', color: ec.color, fontWeight: '600', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{p.redes.map(r => redesSociales.find(x => x.id === r)?.icon || '').join('')} {p.titulo}</div>;
                })}
                {pubs.length > (esCelular ? 1 : 3) && <div style={{ fontSize: '8px', color: colores.grisClaro }}>+{pubs.length - (esCelular ? 1 : 3)}</div>}
              </div>
            );
          })}
        </div>

        {/* Form nueva publicación */}
        {mostrarForm && (
          <>
            <div onClick={() => setMostrarForm(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998 }} />
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: '#fff', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '500px', zIndex: 9999, boxShadow: '0 8px 30px rgba(0,0,0,0.2)', fontFamily: PP, maxHeight: 'calc(var(--beep-vh) * 90)', overflowY: 'auto' }}>
              <div style={{ fontSize: '16px', fontWeight: '700', color: colores.gris, marginBottom: '16px' }}>Nueva publicación</div>
              <div style={{ fontSize: '12px', color: colores.grisClaro, marginBottom: '16px' }}>Completa los datos y el contenido se creará como borrador.</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' }}>
                {redesSociales.map(r => <span key={r.id} style={{ padding: '4px 10px', borderRadius: '8px', background: colores.fondo, fontSize: '11px', color: colores.gris, cursor: 'pointer' }}>{r.icon} {r.label}</span>)}
              </div>
              <span title="El asistente de IA estará disponible próximamente" style={{ display: 'inline-block', padding: '4px 10px', borderRadius: '8px', border: '1px solid #e0e0e0', background: '#f3f4f4', color: '#5e666e', fontSize: '11px', fontWeight: '600', cursor: 'not-allowed', fontFamily: PP, opacity: 0.7, marginBottom: '14px' }}>{'🤖'} Mejorar copy con IA — Próximamente</span>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setMostrarForm(false)} style={{ flex: 1, padding: '10px', background: colores.fondo, color: colores.grisClaro, border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: PP }}>Cancelar</button>
                <button onClick={() => setMostrarForm(false)} style={{ flex: 1, padding: '10px', background: colores.oscuro, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: PP }}>Crear borrador</button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // ========== CAMPAÑAS ==========
  if (tab === 'campanas') {
    if (detallecamp) {
      const c = campanasData.find(x => x.id === detallecamp);
      if (!c) { setDetalleCamp(null); return null; }
      const pubs = publicaciones.filter(p => p.campana === c.nombre);
      const pct = c.presupuesto > 0 ? Math.round((c.gastado / c.presupuesto) * 100) : 0;
      return (
        <div style={{ fontFamily: PP }}>
            <button onClick={() => setDetalleCamp(null)} style={{ background: colores.fondo, border: '1px solid #e0e0e0', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', cursor: 'pointer', color: colores.gris, marginBottom: '16px' }}>{'←'} Volver</button>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '20px', marginBottom: '14px' }}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: colores.gris, marginBottom: '4px' }}>{c.nombre}</div>
            <div style={{ fontSize: '12px', color: colores.grisClaro, marginBottom: '12px' }}>Objetivo: {c.objetivo} · {c.fechaInicio} — {c.fechaFin}</div>
            <div style={{ fontSize: '12px', color: colores.grisClaro, marginBottom: '6px' }}>Presupuesto: ${c.gastado.toLocaleString('es-MX', OPC_DINERO)} / ${c.presupuesto.toLocaleString('es-MX', OPC_DINERO)} ({pct}%)</div>
            <div style={{ height: '8px', background: '#e8e8e8', borderRadius: '4px', overflow: 'hidden', marginBottom: '12px' }}><div style={{ height: '100%', width: `${pct}%`, background: pct > 80 ? colores.rojo : colores.principal, borderRadius: '4px' }} /></div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: colores.gris, marginBottom: '8px' }}>{pubs.length} publicaciones</div>
            {pubs.map(p => { const ec = estatusPub[p.estatus] || estatusPub.borrador; return <div key={p.id} style={{ padding: '8px', background: ec.bg, borderRadius: '8px', marginBottom: '4px', fontSize: '12px', color: ec.color }}>{p.redes.map(r => redesSociales.find(x => x.id === r)?.icon || '').join(' ')} {p.titulo} — {p.fecha}</div>; })}
          </div>
        </div>
      );
    }
    const estCfg = { Activa: { bg: '#e8faf5', color: '#1D9E75' }, Planeación: { bg: '#fffbe6', color: '#7a6e00' }, Pausada: { bg: '#f3f4f4', color: colores.grisClaro }, Finalizada: { bg: '#e6f1fb', color: colores.principal } };
    return (
      <div style={{ fontFamily: PP }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {campanasData.map(c => {
            const ec = estCfg[c.estatus] || estCfg.Planeación;
            const pct = c.presupuesto > 0 ? Math.round((c.gastado / c.presupuesto) * 100) : 0;
            return (
              <div key={c.id} onClick={() => setDetalleCamp(c.id)} style={{ background: '#fff', borderRadius: '14px', padding: '16px', cursor: 'pointer', borderLeft: `4px solid ${ec.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: colores.gris }}>{c.nombre}</div>
                  <span style={{ padding: '3px 10px', borderRadius: '8px', background: ec.bg, color: ec.color, fontSize: '11px', fontWeight: '600' }}>{c.estatus}</span>
                </div>
                <div style={{ fontSize: '12px', color: colores.grisClaro, marginBottom: '6px' }}>{c.objetivo} · {c.canales.map(ch => redesSociales.find(r => r.id === ch)?.icon || '').join(' ')}</div>
                <div style={{ fontSize: '11px', color: colores.grisClaro }}>${c.gastado.toLocaleString('es-MX', OPC_DINERO)} / ${c.presupuesto.toLocaleString('es-MX', OPC_DINERO)} ({pct}%)</div>
                <div style={{ height: '4px', background: '#e8e8e8', borderRadius: '2px', marginTop: '6px' }}><div style={{ height: '100%', width: `${pct}%`, background: ec.color, borderRadius: '2px' }} /></div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ========== BIBLIOTECA ==========
  if (tab === 'biblioteca') {
    const bFiltrados = bibliotecaData.filter(b => {
      if (filtroEstatus === 'archivado' && b.estatus !== 'archivado') return false;
      if (filtroEstatus === 'activo' && b.estatus !== 'activo') return false;
      if (filtroCat && b.categoria !== filtroCat) return false;
      if (busqueda && !b.nombre.toLowerCase().includes(busqueda.toLowerCase())) return false;
      if (!filtroEstatus) return b.estatus === 'activo';
      return true;
    });
    return (
      <div style={{ fontFamily: PP }}>
        <BarraBusqueda value={busqueda} onChange={setBusqueda} placeholder="Buscar por nombre..." />
        <FiltrosRapidos opciones={[{ value: '', label: 'Activos', count: bibliotecaData.filter(b => b.estatus === 'activo').length }, { value: 'archivado', label: 'Archivados', count: bibliotecaData.filter(b => b.estatus === 'archivado').length }]} activo={filtroEstatus} onChange={setFiltroEstatus} />
        <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
          {Object.entries(catCfg).map(([k, v]) => <button key={k} onClick={() => setFiltroCat(filtroCat === k ? '' : k)} style={{ padding: '4px 10px', borderRadius: '16px', border: 'none', fontSize: '10px', fontWeight: '600', cursor: 'pointer', fontFamily: PP, background: filtroCat === k ? v.color + '20' : '#fff', color: filtroCat === k ? v.color : colores.grisClaro, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>{v.icon} {v.label}</button>)}
        </div>
        <ContadorResultados total={bibliotecaData.filter(b => b.estatus === 'activo').length} filtrados={bFiltrados.length} />
        <div style={{ display: 'grid', gridTemplateColumns: esCelular ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '10px', marginTop: '10px' }}>
          {bFiltrados.map(b => {
            const cat = catCfg[b.categoria] || catCfg.documentos;
            return (
              <div key={b.id} style={{ background: '#fff', borderRadius: '12px', padding: '14px', border: '1px solid #f0f0f0', opacity: b.estatus === 'archivado' ? 0.5 : 1 }}>
                <div style={{ fontSize: '32px', textAlign: 'center', marginBottom: '8px' }}>{cat.icon}</div>
                <div style={{ fontSize: '12px', fontWeight: '600', color: colores.gris, marginBottom: '4px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{b.nombre}</div>
                <span style={{ padding: '2px 8px', borderRadius: '6px', background: cat.color + '15', color: cat.color, fontSize: '9px', fontWeight: '600' }}>{cat.label}</span>
                <div style={{ fontSize: '10px', color: colores.grisClaro, marginTop: '6px' }}>{b.subidoPor} · {b.fecha}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ========== RECURSOS COMERCIALES ==========
  if (tab === 'recursos') {
    const secciones = [
      { titulo: 'Materiales de venta', items: [{ nombre: 'Ficha técnica CCTV', desc: 'Resumen de servicios y especificaciones', icon: '📄' }, { nombre: 'Presentación corporativa', desc: '20 slides — última actualización feb 2026', icon: '📊' }, { nombre: 'Caso de éxito Bodegas Alianza', desc: 'PDF con fotos, datos y resultados', icon: '🏆' }] },
      { titulo: 'Identidad corporativa', items: [{ nombre: 'Logo horizontal', desc: 'PNG y SVG sobre blanco', icon: '🏷️' }, { nombre: 'Logo cuadrado', desc: 'Para redes sociales y app', icon: '🏷️' }, { nombre: 'Paleta de colores', desc: '#5c9adb · #2f5f9e · #dcde74 · #5cd4b0 · #e05c5c', icon: '🎨' }, { nombre: 'Tipografía Poppins', desc: 'Fuente oficial — fonts.google.com', icon: '🖥️' }] },
      { titulo: 'Información de servicios', items: [{ nombre: 'CCTV / Videovigilancia', desc: 'Cámaras IP/analógicas, NVR/DVR, acceso remoto', icon: '📹' }, { nombre: 'Control de acceso', desc: 'Facial, huella, tarjeta — ZKTeco', icon: '🔐' }, { nombre: 'Redes e infraestructura', desc: 'Cableado UTP, fibra óptica, WiFi', icon: '🌐' }, { nombre: 'Mantenimiento', desc: 'Preventivo y correctivo — contratos mensuales', icon: '🔧' }] },
    ];
    return (
      <div style={{ fontFamily: PP }}>
        {secciones.map((sec, si) => (
          <div key={si} style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: colores.gris, marginBottom: '10px' }}>{sec.titulo}</div>
            <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : 'repeat(3, 1fr)', gap: '10px' }}>
              {sec.items.map((it, ii) => (
                <div key={ii} style={{ background: '#fff', borderRadius: '12px', padding: '16px', border: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>{it.icon}</div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: colores.gris, marginBottom: '4px' }}>{it.nombre}</div>
                  <div style={{ fontSize: '11px', color: colores.grisClaro, lineHeight: 1.4, marginBottom: '10px' }}>{it.desc}</div>
                  <button style={{ padding: '6px 12px', background: colores.fondo, color: colores.grisClaro, border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: PP }}>Descargar</button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ========== CREDENCIALES ==========
  if (tab === 'credenciales' && esMarketing) {
    const diasDesde = (f) => Math.floor((new Date() - new Date(f)) / 86400000);
    return (
      <div style={{ fontFamily: PP }}>
        <div style={{ background: '#fffbe6', borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', fontSize: '12px', color: '#7a6e00', border: '1px solid #dcde74' }}>{'🔒'} Información confidencial. Todo acceso queda registrado.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {plataformasData.map(p => {
            const antiguo = diasDesde(p.ultimoCambio) > 90;
            const inactiva = p.estatus === 'Inactiva';
            return (
              <div key={p.id} style={{ background: '#fff', borderRadius: '12px', padding: '14px 16px', opacity: inactiva ? 0.6 : 1, borderLeft: `4px solid ${inactiva ? '#d0d0d0' : antiguo ? colores.rojo : colores.verde}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', flexWrap: 'wrap', gap: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px' }}>{p.icon}</span>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: colores.gris }}>{p.nombre}</div>
                      <div style={{ fontSize: '11px', color: colores.grisClaro }}>{p.categoria} · {p.responsable}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    {inactiva && <span style={{ padding: '2px 8px', borderRadius: '6px', background: '#f3f4f4', color: colores.grisClaro, fontSize: '10px', fontWeight: '600' }}>Inactiva</span>}
                    {antiguo && !inactiva && <span style={{ padding: '2px 8px', borderRadius: '6px', background: '#fde8e8', color: colores.rojo, fontSize: '10px', fontWeight: '600' }}>{diasDesde(p.ultimoCambio)}d sin cambiar</span>}
                    <a href={p.url} target="_blank" rel="noopener noreferrer" style={{ padding: '4px 10px', borderRadius: '6px', background: '#e6f1fb', color: colores.oscuro, fontSize: '10px', fontWeight: '600', textDecoration: 'none' }}>Abrir</a>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: '6px', fontSize: '12px' }}>
                  <div><span style={{ color: colores.grisClaro }}>Usuario: </span><span style={{ color: colores.gris, fontWeight: '500' }}>{p.usuario}</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ color: colores.grisClaro }}>Contraseña: </span>
                    <span style={{ color: colores.gris, fontWeight: '500', fontFamily: 'monospace' }}>{passVisible[p.id] ? p.password : '••••••••'}</span>
                    {esAdmin && <span onClick={() => setPassVisible(prev => ({ ...prev, [p.id]: !prev[p.id] }))} style={{ cursor: 'pointer', fontSize: '14px' }}>{passVisible[p.id] ? '🙈' : '👁️'}</span>}
                  </div>
                </div>
                {p.notas && <div style={{ fontSize: '11px', color: colores.grisClaro, marginTop: '6px', fontStyle: 'italic' }}>{p.notas}</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ========== MÉTRICAS ==========
  if (tab === 'metricas') {
    const porRed = redesSociales.slice(0, 6).map(r => ({ name: r.label, count: publicaciones.filter(p => p.redes.includes(r.id)).length, color: r.color }));
    const porMes = [{ mes: 'Ene', pub: 8 }, { mes: 'Feb', pub: 10 }, { mes: 'Mar', pub: publicaciones.length }];
    return (
      <div style={{ fontFamily: PP }}>
        <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
          {[{ l: 'Publicaciones del mes', v: publicaciones.length, c: colores.principal }, { l: 'Campañas activas', v: campanasData.filter(c => c.estatus === 'Activa').length, c: '#1D9E75' }, { l: 'Presupuesto gastado', v: '$' + campanasData.reduce((a, c) => a + c.gastado, 0).toLocaleString('es-MX', OPC_DINERO), c: '#f4a261' }].map((k, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: '800', color: k.c }}>{k.v}</div>
              <div style={{ fontSize: '11px', color: colores.grisClaro, marginTop: '4px' }}>{k.l}</div>
            </div>
          ))}
        </div>
        <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', marginBottom: '14px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: colores.gris, marginBottom: '12px' }}>Publicaciones por red social</div>
          <div style={{ height: '200px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={porRed} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: colores.grisClaro }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: colores.grisClaro }} axisLine={false} tickLine={false} width={28} />
                <Tooltip />
                <Bar dataKey="count" name="Publicaciones" radius={[5, 5, 0, 0]}>
                  {porRed.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div style={{ background: '#fff', borderRadius: '12px', padding: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: colores.gris, marginBottom: '12px' }}>Publicaciones por mes</div>
          <div style={{ height: '180px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={porMes} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="mes" tick={{ fontSize: 10, fill: colores.grisClaro }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: colores.grisClaro }} axisLine={false} tickLine={false} width={28} />
                <Tooltip />
                <Bar dataKey="pub" name="Publicaciones" fill={colores.principal} radius={[5, 5, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div style={{ marginTop: '14px', padding: '14px', background: '#f3effe', borderRadius: '10px', fontSize: '12px', color: '#6d28d9', textAlign: 'center' }}>Conecta tus redes para ver métricas en tiempo real — Próximamente</div>
      </div>
    );
  }

  // ========== AGENTES IA ==========
  if (tab === 'agentes') {
    const agente = agentesIA.find(a => a.id === agenteActivo) || agentesIA[0];
    const mensajes = chatsAgentes[agenteActivo] || [];
    const luna = agentesIA.find(a => a.id === 'luna');
    const otros = agentesIA.filter(a => a.id !== 'luna');

    // Vista de chat abierto
    if (chatAbierto) {
      return (
        <div style={{ fontFamily: PP }}>
            <style>{ESTILOS_AGENTES_IA}</style>

          <div style={{
            background: agente.bg, borderRadius: 20, padding: esCelular ? 14 : 18,
            display: 'flex', flexDirection: 'column', height: esCelular ? '72vh' : '640px',
            boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
          }}>
            {/* Header del chat con botito */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <button
                onClick={() => setChatAbierto(false)}
                style={{
                  background: 'rgba(255,255,255,0.6)', border: 'none', width: 34, height: 34,
                  borderRadius: '50%', fontSize: 18, cursor: 'pointer', color: agente.nameColor,
                  fontFamily: PP, flexShrink: 0,
                }}
              >←</button>
              <BotitoIA agente={agente} estado={agente.estado} animDelay={0} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: agente.nameColor, lineHeight: 1.1 }}>
                  {agente.nombre}
                </div>
                <div style={{ fontSize: 11, color: agente.roleColor, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 2 }}>
                  {agente.rol}
                </div>
                <div style={{ marginTop: 6 }}>
                  <BadgeEstadoIA estado={agente.estado} />
                </div>
              </div>
              {mensajes.length > 0 && (
                <button
                  onClick={() => setChatsAgentes(prev => ({ ...prev, [agenteActivo]: [] }))}
                  style={{
                    padding: '6px 10px', background: 'rgba(255,255,255,0.6)', color: agente.roleColor,
                    border: 'none', borderRadius: 8, fontSize: 10, fontWeight: 700,
                    cursor: 'pointer', fontFamily: PP,
                  }}
                >Limpiar</button>
              )}
            </div>

            {/* Mensajes */}
            <div style={{
              flex: 1, overflowY: 'auto', padding: 14, background: 'rgba(255,255,255,0.55)',
              borderRadius: 14, display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              {mensajes.length === 0 && (
                <div style={{ textAlign: 'center', color: agente.roleColor, fontSize: 12, marginTop: 40 }}>
                  <div style={{ fontSize: 40, marginBottom: 10 }}>{agente.emoji}</div>
                  <div>Escribe un mensaje para iniciar la conversación con {agente.nombre}</div>
                </div>
              )}
              {mensajes.map((m, i) => (
                <div key={i} style={{
                  alignSelf: m.autor === 'usuario' ? 'flex-end' : 'flex-start',
                  maxWidth: '80%', padding: '8px 12px', borderRadius: 12,
                  background: m.autor === 'usuario' ? agente.btnBg : 'rgba(255,255,255,0.85)',
                  color: m.autor === 'usuario' ? '#fff' : agente.nameColor,
                  fontSize: 13, lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                }}>{m.texto}</div>
              ))}
              {enviandoAgente && (
                <div style={{
                  alignSelf: 'flex-start', padding: '8px 12px', borderRadius: 12,
                  background: 'rgba(255,255,255,0.85)', color: agente.roleColor,
                  fontSize: 12, fontStyle: 'italic', display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
                  {agente.nombre} está escribiendo
                  <span style={{ display: 'inline-flex', gap: 2 }}>
                    {[0, 1, 2].map(i => (
                      <span key={i} style={{
                        width: 4, height: 4, borderRadius: '50%', background: agente.roleColor,
                        animation: `marketing-typing-dot 1s ease-in-out infinite`, animationDelay: `${i * 0.15}s`,
                      }} />
                    ))}
                  </span>
                </div>
              )}
            </div>

            {/* Input */}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <input
                value={inputAgente}
                onChange={e => setInputAgente(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviarMensajeAgente(); } }}
                placeholder={`Escribe a ${agente.nombre}...`}
                disabled={enviandoAgente}
                style={{
                  flex: 1, padding: '11px 14px', borderRadius: 12,
                  border: 'none', fontSize: 13, outline: 'none', fontFamily: PP,
                  background: 'rgba(255,255,255,0.85)', color: agente.nameColor,
                }}
              />
              <button
                onClick={enviarMensajeAgente}
                disabled={enviandoAgente || !inputAgente.trim()}
                style={{
                  padding: '11px 18px', background: agente.btnBg, color: '#fff', border: 'none', borderRadius: 12,
                  fontSize: 13, fontWeight: 700, fontFamily: PP,
                  cursor: (enviandoAgente || !inputAgente.trim()) ? 'not-allowed' : 'pointer',
                  opacity: (enviandoAgente || !inputAgente.trim()) ? 0.5 : 1,
                }}
              >{enviandoAgente ? '...' : 'Enviar'}</button>
            </div>
          </div>
        </div>
      );
    }

    // Vista grid de tarjetas (estilo CentroAgentes)
    return (
      <div style={{ fontFamily: PP }}>
        <style>{ESTILOS_AGENTES_IA}</style>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontSize: esCelular ? 20 : 24, fontWeight: 800, color: '#1a1a1a' }}>Agentes de marketing</div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>Tu equipo de IA para contenido y campañas</div>
          </div>
          <span style={{
            background: '#D1FAE5', color: '#065F46',
            padding: '6px 14px', borderRadius: 999,
            fontSize: 12, fontWeight: 800,
            display: 'inline-flex', alignItems: 'center', gap: 6,
            animation: 'marketing-pulse-ring 2s ease-out infinite',
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10B981' }} />
            En vivo
          </span>
        </div>

        {/* Luna ancho completo */}
        <div style={{ marginBottom: 14 }}>
          <CardAgenteIA agente={luna} animDelay={0} movil={esCelular} onAbrirChat={abrirChatAgente} />
        </div>

        {/* Grid 2x2 para Stella, Spark, Iris, Dato */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr',
          gap: 14,
        }}>
          {otros.map((a, i) => (
            <CardAgenteIA key={a.id} agente={a} animDelay={i * 0.25} movil={esCelular} onAbrirChat={abrirChatAgente} />
          ))}
        </div>
      </div>
    );
  }

  // Fallback
  return (
    <div style={{ fontFamily: PP }}>
      <div style={{ textAlign: 'center', padding: '40px', color: colores.grisClaro }}>Selecciona una pestaña</div>
    </div>
  );
}

export default Marketing;
